import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useKanbanApi } from "@/utils/kanban-api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  KanbanCard,
  NegociacaoFreelancer,
  StatusNegociacaoFreelancer,
} from "@/types/kanban";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Handshake, Calendar, MessageSquare, User } from "lucide-react";
import { reportUserError } from "@/lib/report-error";

interface NegociacaoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: KanbanCard | null;
}

const STATUS_OPTIONS: { value: StatusNegociacaoFreelancer; label: string }[] = [
  { value: "oferta_feita", label: "Oferta feita" },
  { value: "topou", label: "Topou" },
  { value: "contra_proposta", label: "Contra-proposta" },
  { value: "contra_proposta_aceita", label: "Contra-proposta aceita" },
  { value: "aguardando_resposta_agencia", label: "Aguardando resposta da agência" },
  { value: "recusou", label: "Recusou" },
  { value: "desistiu", label: "Desistiu" },
];

const STATUS_COLORS: Record<StatusNegociacaoFreelancer, string> = {
  oferta_feita: "bg-blue-50 text-blue-700 border-blue-200",
  topou: "bg-green-50 text-green-700 border-green-200",
  contra_proposta: "bg-amber-50 text-amber-700 border-amber-200",
  contra_proposta_aceita: "bg-emerald-50 text-emerald-700 border-emerald-200",
  aguardando_resposta_agencia: "bg-slate-50 text-slate-700 border-slate-200",
  recusou: "bg-red-50 text-red-700 border-red-200",
  desistiu: "bg-red-50 text-red-700 border-red-200",
};

function statusLabel(status: StatusNegociacaoFreelancer): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

function formatBRL(centavos?: number | null): string {
  if (centavos === null || centavos === undefined) return "—";
  return (centavos / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function NegociacaoModal({
  open,
  onOpenChange,
  candidate,
}: NegociacaoModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const kanbanApi = useKanbanApi();

  const posicaoId = candidate?.id;
  const vagaId = candidate?.vaga_id;

  // Form state
  const [status, setStatus] = useState<StatusNegociacaoFreelancer>("oferta_feita");
  const [valor, setValor] = useState<string>(""); // BRL como string, ex "1500,00"
  const [dataDisponibilidade, setDataDisponibilidade] = useState<string>("");
  const [observacao, setObservacao] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const { data: negociacoes = [], isLoading } = useQuery({
    queryKey: ["kanban-negociacoes", posicaoId],
    queryFn: () => (posicaoId ? kanbanApi.getNegociacoes(posicaoId) : []),
    enabled: !!posicaoId && open,
  });

  const resetForm = () => {
    setStatus("oferta_feita");
    setValor("");
    setDataDisponibilidade("");
    setObservacao("");
  };

  const handleSave = async () => {
    if (!posicaoId) return;

    // Converte valor BRL ("1.500,50" ou "1500.50") em centavos
    let valorCentavos: number | null = null;
    const valorTrim = valor.trim();
    if (valorTrim) {
      const normalized = valorTrim.replace(/\./g, "").replace(",", ".");
      const num = Number(normalized);
      if (Number.isFinite(num) && num >= 0) {
        valorCentavos = Math.round(num * 100);
      } else {
        toast({
          title: "Valor inválido",
          description: "Use formato 1500,00 ou deixe em branco.",
          variant: "destructive",
        });
        return;
      }
    }

    setIsSaving(true);
    try {
      await kanbanApi.addNegociacao(
        posicaoId,
        {
          status,
          valor: valorCentavos,
          data_disponibilidade: dataDisponibilidade || null,
          observacao: observacao.trim() || null,
        },
        user?.id
      );

      // Invalidar queries dependentes
      queryClient.invalidateQueries({ queryKey: ["kanban-negociacoes", posicaoId] });
      if (vagaId) {
        queryClient.invalidateQueries({ queryKey: ["kanban-negociacoes-atuais", vagaId] });
        queryClient.invalidateQueries({ queryKey: ["vagas-card-metricas"] });
      }

      toast({
        title: "Negociação registrada",
        description: `Status atual: ${statusLabel(status)}.`,
      });
      resetForm();
    } catch (error) {
      const err = error as Error;
      console.error("Erro ao registrar negociação:", err);
      toast({
        title: "Erro",
        description: "Não foi possível registrar a negociação. " + reportUserError(err, { source: "kanban-negociacao" }).description,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake className="h-5 w-5 text-blue-600" />
            Negociação · {candidate?.freelancer?.nome ?? "Freelancer"}
          </DialogTitle>
          <DialogDescription>
            Registre o andamento da negociação com este freelancer. A última
            entrada vira o status atual.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Histórico */}
          <div className="flex-1 min-h-[120px]">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Histórico ({negociacoes.length})
            </h3>
            <ScrollArea className="h-[240px] rounded-md border border-slate-200 bg-slate-50/50 p-2">
              {isLoading ? (
                <p className="text-sm text-slate-500 p-2">Carregando...</p>
              ) : negociacoes.length === 0 ? (
                <p className="text-sm text-slate-500 p-2">
                  Nenhuma negociação registrada ainda.
                </p>
              ) : (
                <ul className="space-y-2">
                  {negociacoes.map((neg: NegociacaoFreelancer, idx: number) => (
                    <li
                      key={neg.id}
                      className={`rounded-md border p-3 bg-white ${
                        idx === 0
                          ? "border-blue-300 ring-2 ring-blue-100"
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`text-xs ${STATUS_COLORS[neg.status]}`}
                          >
                            {statusLabel(neg.status)}
                          </Badge>
                          {idx === 0 && (
                            <Badge
                              variant="default"
                              className="text-[10px] bg-blue-600"
                            >
                              ATUAL
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">
                          {format(new Date(neg.created_at), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </span>
                      </div>
                      <div className="text-xs text-slate-700 space-y-1 mt-2">
                        {neg.valor != null && (
                          <div className="font-medium">
                            Valor: {formatBRL(neg.valor)}
                          </div>
                        )}
                        {neg.data_disponibilidade && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-slate-500" />
                            Disponível a partir de{" "}
                            {format(
                              new Date(neg.data_disponibilidade + "T00:00"),
                              "dd/MM/yyyy"
                            )}
                          </div>
                        )}
                        {neg.observacao && (
                          <div className="flex items-start gap-1">
                            <MessageSquare className="h-3 w-3 text-slate-500 mt-0.5" />
                            <span className="whitespace-pre-wrap">
                              {neg.observacao}
                            </span>
                          </div>
                        )}
                        {neg.registrado_por && (
                          <div className="flex items-center gap-1 text-slate-400">
                            <User className="h-3 w-3" />
                            por {neg.registrado_por.slice(0, 8)}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </div>

          {/* Form nova negociação */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Registrar nova negociação
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="neg-status" className="text-xs">
                  Status
                </Label>
                <Select
                  value={status}
                  onValueChange={(v) =>
                    setStatus(v as StatusNegociacaoFreelancer)
                  }
                >
                  <SelectTrigger id="neg-status" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="neg-valor" className="text-xs">
                  Valor (R$)
                </Label>
                <Input
                  id="neg-valor"
                  placeholder="1500,00"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  className="h-9"
                />
              </div>

              <div>
                <Label htmlFor="neg-data" className="text-xs">
                  Disponível a partir de
                </Label>
                <Input
                  id="neg-data"
                  type="date"
                  value={dataDisponibilidade}
                  onChange={(e) => setDataDisponibilidade(e.target.value)}
                  className="h-9"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="neg-obs" className="text-xs">
                  Observação
                </Label>
                <Textarea
                  id="neg-obs"
                  placeholder="Detalhes da negociação (opcional)"
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Fechar
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Registrando..." : "Registrar negociação"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
